import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './ModuleThree.css'

const MODULE_ID = 3
const SECTION = 'DroneSection'

// TODO: swap for the actual Module 3 video URL
const VIDEO_URL = 'https://res.cloudinary.com/dk50cmtps/video/upload/v1780034028/drone_yzbva8.mp4'

// TODO: swap these for questions that match Module 3's actual video content —
// these are placeholders following the same drone-topic thread as Module 1/2.
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'UAV-ல் GPS-denied navigation-க்கு பொதுவா எது use பண்றாங்க?',
    options: [
      { key: 'a', label: 'Inertial Navigation System (INS)' },
      { key: 'b', label: 'Bluetooth' },
      { key: 'c', label: 'Wi-Fi signal' },
      { key: 'd', label: 'FM Radio' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q2',
    question: 'Drone swarm operation-ல முக்கிய challenge எது?',
    options: [
      { key: 'a', label: 'Battery colour' },
      { key: 'b', label: 'Inter-drone communication & coordination' },
      { key: 'c', label: 'Camera resolution மட்டும்' },
      { key: 'd', label: 'Propeller size' },
    ],
    correctKey: 'b',
  },
  {
    id: 'q3',
    question: 'Counter-drone systems-ல "soft-kill" method-க்கு உதாரணம்?',
    options: [
      { key: 'a', label: 'Interceptor drone collision' },
      { key: 'b', label: 'RF jamming / GNSS disruption' },
      { key: 'c', label: 'Physical net capture மட்டும்' },
      { key: 'd', label: 'Laser cutting' },
    ],
    correctKey: 'b',
  },
]

const ModuleThree = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as Module 1 & 2.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so Module 4
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 3 complete — Module 4 unlocked!')
    navigate('/drone_course')
  }

  return (
    <div className="module_three_pg_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="module_three_pg_content">
        <h2 className="module_three_pg_title">Module 3: Deep Dive</h2>
        <p className="module_three_pg_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 4.
        </p>

        <div className="module_three_pg_video_wrap">
          <video
            className="module_three_pg_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="module_three_pg_progress_row">
          <div className="module_three_pg_progress_track">
            <div
              className="module_three_pg_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="module_three_pg_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="module_three_pg_assessment">
          <h3 className="module_three_pg_assessment_title">Module Assessment</h3>
          <p className="module_three_pg_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="module_three_pg_question_card" key={q.id}>
                <p className="module_three_pg_question_text">
                  <span className="module_three_pg_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="module_three_pg_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'module_three_pg_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'module_three_pg_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'module_three_pg_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`module_three_pg_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="module_three_pg_option_key">{opt.key.toUpperCase()}</span>
                        <span className="module_three_pg_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="module_three_pg_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="module_three_pg_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModuleThree